import React, { useState, useEffect, useCallback } from 'react';
import { Input, Button, Card, Typography, Alert, Space, Steps, Divider, Modal } from 'antd';
import { useSearchParams } from 'react-router-dom';
import { VehicleFormData, VehicleHistoryReport, ImageObject } from '../../types/vehicle';
import { ImageReview } from './image_review';
import { VehicleDetailsReviewPage } from './vehicle_details_review_page';
import HistoryReportReview from './history_report_review';
import CreatePpiReport from '../ppi-report/create_ppi_report';

import { authPpiApiAxiosWithRefresh, getPrices } from '../../services/api';
import { useUser } from '../../context/UserContext';

import { PpiAnalysisProvider } from '../ppi-report/contexts/PpiAnalysisContext';
import TopBar from '../TopBar/TopBar';
import './web_scraper_custom_ppi.scss';

const { Title, Text } = Typography;




const steps = [
  { title: 'Get Started', description: 'Enter a URL or add details yourself' },
  { title: 'Vehicle Details', description: 'Add and confirm vehicle information' },
  { title: 'Images', description: 'Add Photos (optional)' },
  { title: 'History Reports', description: 'Upload a History Report' },
  { title: 'Generate Report', description: 'Get AI-Powered Inspection Report' }
];

const WebScraperCustomPPIRevamped: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(0);
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Analyzing vehicle details. This may take a few minutes...');
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<VehicleFormData | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalAction, setModalAction] = useState<'scrape' | 'manual' | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { credits } = useUser();
  const [prices, setPrices] = useState({ quick_run_cost: 0, full_run_cost: 5 });

  const [vinInput, setVinInput] = useState('');
  const [isManual, setIsManual] = useState(false);

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const pricesData = await getPrices();
        if (pricesData.quick_run_cost && pricesData.full_run_cost) {
          setPrices(pricesData);
        }
      } catch (error) {
        console.error("Failed to fetch prices", error);
      }
    };
    fetchPrices();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const hasEnoughCredits = credits >= prices.full_run_cost;
  // Get docId from URL params
  const docId = searchParams.get('docId');


  // Function to load formData from Firestore
  const loadFormData = useCallback(async () => {
    if (!docId) return;
    setLoading(true);
    try {
      const response = await (await authPpiApiAxiosWithRefresh()).get(`/vehicle-data/${docId}`);

      if (response.data.success) {
        setIsManual(response.data.isManual || false);
        const fetchedData = response.data.data;
        const vehicleDetails = fetchedData.vehicleDetails || {};

        // --- START: New Image Handling Logic ---
        let scrapedImages: ImageObject[] = fetchedData.scraped_image_links_gcs || [];
        const originalScrapedUrls: string[] = (fetchedData.vehicleDetails && fetchedData.vehicleDetails.imageLinks) || [];

        // If GCS links are not populated yet, but original links exist, use them as a fallback
        if (scrapedImages.length === 0 && originalScrapedUrls.length > 0) {
          scrapedImages = originalScrapedUrls.map(url => ({
            url: url,
            status: 'pending_classification', // Status is technically unknown from this fallback
            category: null
          }));
        }

        const uploadedImages: ImageObject[] = fetchedData.user_uploaded_images || [];
        const allImageLinks: ImageObject[] = [...scrapedImages, ...uploadedImages];
        // --- END: New Image Handling Logic ---

        const historyReports = fetchedData.historyReports || [];

        setFormData({
          ...vehicleDetails,
          description: fetchedData.vehicleDescription || '',
          comments: fetchedData.userComments || '',
          imageLinks: allImageLinks,
          historyReports: historyReports,
        });



      } else {
        setError(response.data.error || 'Failed to load vehicle data.');
      }
    } catch (err) {
      setError('Error loading vehicle data.');
      console.error('Error loading vehicle data:', err);
    } finally {
      setLoading(false);
    }
  }, [docId]);

  // Effect to load formData if docId is present
  useEffect(() => {
    if (docId) {
      loadFormData();
    }

    const step = searchParams.get('step');
    if (step && !isNaN(Number(step))) {
      setCurrentStep(Number(step));
    } else if (docId) {
      // If docId exists but no step, start at vehicle details
      setCurrentStep(1);
    }
  }, [searchParams, docId, loadFormData]); // Added loadFormData to dependency array

  useEffect(() => {
    const step = searchParams.get('step');
    if (step && !isNaN(Number(step))) {
      setCurrentStep(Number(step));
    } else if (docId) {
      // If docId exists but no step, start at vehicle details
      setCurrentStep(1);
    } else {
      // If no docId and no step, reset to start
      setCurrentStep(0);
    }
  }, [searchParams, docId]);

  useEffect(() => {
    if (loading) {
      setLoadingMessage('Analyzing vehicle details. This may take a few minutes...');
      const timer1 = setTimeout(() => {
        setLoadingMessage('Gathering vehicle details and description...');
      }, 15000);
      const timer2 = setTimeout(() => {
        setLoadingMessage('Gathering images...');
      }, 30000);
      const timer3 = setTimeout(() => {
        setLoadingMessage('Almost there, hold on while we validate results...');
      }, 45000);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    }
  }, [loading]);

  const handleContinue = () => {
    if (docId) {
      updateStep(1); // Go to the next step
    }
  };



  // Helper function to update both step state and URL
  const updateStep = (step: number) => {
    setCurrentStep(step);
    if (docId) {
      setSearchParams({ docId, step: step.toString() });
    }
  };

  const handleScrape = async (confirmed = false) => {
    if (docId && !confirmed) {
      setModalAction('scrape');
      setModalVisible(true);
      return;
    }

    if (!url) {
      setError('Please enter a URL');
      return;
    }

    setLoading(true);
    setError(null);
    setModalVisible(false);

    try {
      const response = await (
        await authPpiApiAxiosWithRefresh()
      ).post('/scrape-webpage-custom-ppi', {
        url: url
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });


      if (response.data.success) {
        setFormData(response.data.formData);
        const newDocId = response.data.firestore.doc_id;

        // Replace current history entry with step 0 (so back button goes here)
        setSearchParams({ docId: newDocId, step: '0' }, { replace: true });

        // Push step 1 to history
        setTimeout(() => {
          setSearchParams({ docId: newDocId, step: '1' });
          setCurrentStep(1);
        }, 0);
      } else {
        setError(response.data.error || 'Failed to scrape webpage');
      }
    } catch (err: any) {
      if (err.response && err.response.data && err.response.data.error) {
        setError(err.response.data.error);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An error occurred while scraping the webpage');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleManualEntry = async (confirmed = false, vin?: string) => {
    if (docId && !confirmed) {
      setModalAction('manual');
      setModalVisible(true);
      return;
    }

    if (vin && vin.length !== 17) {
      setError('Please enter a valid 17-character VIN.');
      return;
    }

    setLoading(true);
    setError(null);
    setModalVisible(false);

    try {
      const response = await (
        await authPpiApiAxiosWithRefresh()
      ).post('/create-manual-ppi', {
        vin: vin
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.data.success) {
        const newDocId = response.data.doc_id;
        setFormData({} as VehicleFormData);

        // Replace current history entry with step 0 (so back button goes here)
        setSearchParams({ docId: newDocId, step: '0' }, { replace: true });

        // Push step 1 to history
        setTimeout(() => {
          setSearchParams({ docId: newDocId, step: '1' });
          setCurrentStep(1);
        }, 0);
      } else {
        setError(response.data.error || 'Failed to create manual report.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while creating the report.');
    } finally {
      setLoading(false);
    }
  };

  const handleVehicleDetailsSubmit = (data: VehicleFormData) => {
    setFormData(data);
    updateStep(2); // Go directly to images page
  };

  const handleImageSubmit = (images: ImageObject[]) => {
    if (formData) {
      setFormData({ ...formData, imageLinks: images });
      updateStep(3);
    }
  };

  const handleHistoryReportSubmit = (reports: VehicleHistoryReport[]) => {
    if (formData) {
      setFormData({ ...formData, historyReports: reports });
      updateStep(4);
    }
  };

  const handleReportComplete = () => {
    updateStep(0);
    setFormData(null);
    setUrl('');
    setVinInput('');
    // Clear URL params
    setSearchParams({});
  };

  const goBack = () => {
    if (currentStep > 0) {
      updateStep(currentStep - 1);
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div>
            {docId && (
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <Button
                  type="primary"
                  size="large"
                  onClick={handleContinue}
                  disabled={loading}
                >
                  Continue with Existing Report
                </Button>
              </div>
            )}

            {loading && (
              <Alert
                message="⚡ Please keep this page open while we finish gathering your data. We're gathering listing details, which may take up to 1-2 minutes."
                type="warning"
                style={{ marginBottom: 24 }}
              />
            )}
            <Card className="custom-card">

              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <Title level={3}>Enter Vehicle Listing URL</Title>
                <Text type="secondary">
                  Just drop the URL —— we'll pull the details and build your report instantly.
                </Text>
              </div>

              <Space direction="vertical" style={{ width: '100%' }} size="large">
                <Input
                  size="large"
                  placeholder="Paste a listing URL (e.g., Bring a Trailer, Cars and Bids, Autotrader)"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onPressEnter={() => handleScrape()}
                  disabled={loading}
                />

                <div style={{ textAlign: 'center' }}>
                  <Space direction="vertical">
                    <Button
                      type="primary"
                      size="large"
                      onClick={() => handleScrape()}
                      loading={loading}
                      disabled={!url || loading || !hasEnoughCredits}
                      style={{ whiteSpace: 'normal', height: 'auto' }}
                    >
                      {loading ? loadingMessage : (docId ? 'Generate another Report' : 'Generate a Report')}
                    </Button>
                    <Text type="secondary">
                      Cost: {prices.full_run_cost} credit{prices.full_run_cost !== 1 ? 's' : ''}.
                      {!hasEnoughCredits && " You don't have enough credits."}
                    </Text>
                    {error && (
                      <Alert
                        message=""
                        description={error}
                        type="error"
                        showIcon
                        style={{ marginTop: 16, textAlign: 'left' }}
                      />
                    )}
                  </Space>
                </div>

                <Divider style={{ borderColor: 'black', borderWidth: '2px' }}>OR</Divider>

                <div style={{ textAlign: 'center' }}>
                  <Title level={4}>No link? Enter a VIN to fill details.</Title>
                  <Text type="secondary">
                    Use this for trade offers, in-person inspections, or if your URL isn't automatically supported.
                  </Text>
                  <div style={{ marginTop: 16 }}>
                    <Space direction="vertical" style={{ width: '100%', maxWidth: '400px' }}>
                      <Input
                        size="large"
                        placeholder="Enter VIN (17 characters)"
                        value={vinInput}
                        onChange={(e) => setVinInput(e.target.value.toUpperCase())}
                        maxLength={17}
                        disabled={loading}
                      />
                      <Button
                        type="primary"
                        size="large"
                        onClick={() => handleManualEntry(false, vinInput)}
                        loading={loading}
                        disabled={loading || !hasEnoughCredits || vinInput.length !== 17}
                      >
                        {loading ? 'Creating Report...' : (docId ? 'Build another Report with VIN' : 'Build Report with VIN')}
                      </Button>

                      <Button
                        type="default"
                        size="large"
                        onClick={() => handleManualEntry()}
                        loading={loading}
                        disabled={loading || !hasEnoughCredits}
                      >
                        {loading ? 'Creating Report...' : (docId ? 'Build another Report manually' : 'Build manually')}
                      </Button>

                      <Text type="secondary">
                        Cost: {prices.full_run_cost} credit{prices.full_run_cost !== 1 ? 's' : ''}.
                        {!hasEnoughCredits && " You don't have enough credits."}
                      </Text>
                    </Space>
                  </div>
                </div>
              </Space>

              <Modal
                title="Warning"
                visible={modalVisible}
                onOk={() => modalAction === 'scrape' ? handleScrape(true) : handleManualEntry(true, modalAction === 'manual' && vinInput ? vinInput : undefined)}
                onCancel={() => setModalVisible(false)}
                okText="Proceed"
                cancelText="Cancel"
                confirmLoading={loading}
              >
                <p>Proceeding will erase the existing report. Are you sure you want to continue?</p>
              </Modal>
            </Card>

            {loading && (
              <Alert
                message="⚡ Please keep this page open while we finish gathering your data. We're gathering listing details, which may take up to 1-2 minutes."
                type="warning"
                style={{ marginTop: 24 }}
              />
            )}

            {docId && (
              <div style={{ textAlign: 'center', marginTop: 24 }}>
                <Button
                  type="primary"
                  size="large"
                  onClick={handleContinue}
                  disabled={loading}
                >
                  Continue with Existing Report
                </Button>
              </div>
            )}
          </div>
        );

      case 1:
        return docId ? (
          <VehicleDetailsReviewPage
            docId={docId}
            onNext={(vehicleData) => handleVehicleDetailsSubmit(vehicleData)}
            onBack={goBack}
          />
        ) : null;

      case 2:
        return formData ? (
          <ImageReview
            initialImages={formData.imageLinks || []}
            onSubmit={handleImageSubmit}
            onBack={goBack}
            docId={docId || undefined}
            onUploadSuccess={loadFormData}
            isManual={false}
          />
        ) : null;

      case 3:
        return formData && docId ? (
          <HistoryReportReview
            docId={docId}
            vin={formData.VIN || ''}
            initialReports={formData.historyReports || []}
            onSubmit={handleHistoryReportSubmit}
            onBack={goBack}
            onGoToVehicleDetails={() => updateStep(1)}
            isManual={isManual}
          />
        ) : null;

      case 4:
        return docId ? (
          <CreatePpiReport
            docId={docId}
            onComplete={handleReportComplete}
            onBack={goBack}
            vehicleDetails={formData || undefined}
          />
        ) : null;

      default:
        return null;
    }
  };



  return (
    <div>

      <TopBar className="title" title="" />
      <PpiAnalysisProvider docId={docId}>
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: 24 }}>
          {(!isMobile || currentStep === 0) && (
            <Title level={2}>Create your AI-powered Condition Report</Title>
          )}

          {!isMobile && (
            <Steps
              current={currentStep}
              items={steps}
              style={{ marginBottom: 32 }}
            />
          )}

          {renderCurrentStep()}
        </div>
      </PpiAnalysisProvider>
    </div>
  );
};

export default WebScraperCustomPPIRevamped;